import { useEffect } from "react";
import PropTypes from "prop-types";
import { Modal } from "@/config/antd";
import { CloseOutlined } from "@/config/antd";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSecurityQuestionsAsync,
  fetchUserRecoveryQuestionsAsync,
  selectSettingsLoading,
  selectSecurityQuestions,
  selectUserRecoveryQuestions,
} from "@/store/redux/slices/settings/settings_slice";

const AddSecurityQuestion = ({ isVisible, onClose, onSelect }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectSettingsLoading);
  const securityQuestions = useSelector(selectSecurityQuestions);
  const userRecoveryQuestions = useSelector(selectUserRecoveryQuestions);

  useEffect(() => {
    if (isVisible) {
      dispatch(fetchSecurityQuestionsAsync());
      dispatch(fetchUserRecoveryQuestionsAsync());
    }
  }, [isVisible, dispatch]);

  const getQuestionIdFromRecoveryItem = (item) => {
    if (!item || typeof item !== "object") return null;
    if (item.question && typeof item.question === "object" && item.question._id) {
      return item.question._id;
    }
    return (
      item.question ||
      item.questionID ||
      item.recoveryQuestionID ||
      item.recoveryQuestion?._id ||
      null
    );
  };

  // Create a mapping of user's already selected question IDs so we can
  // visually disable them in the list.
  const userAddedQuestionIds = userRecoveryQuestions
    .map(getQuestionIdFromRecoveryItem)
    .filter(Boolean);

  const handleQuestionSelect = (question) => {
    // Prevent selecting questions the user already has configured
    if (userAddedQuestionIds.includes(question._id)) {
      return;
    }

    // Hand the selected question back to the parent so it can
    // immediately open the answer modal. We intentionally do NOT close
    // this modal here so that the "Add Security Questions" dialog
    // remains open in the background while the answer modal is shown.
    onSelect(question);
  };

  const CheckIcon = () => (
    <svg
      className="w-3 h-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={810}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          {/* Header */}
          <div className="p-4 flex items-center justify-between">
            <div className="flex flex-col">
              <h2 className="text-bold-x text-primary">
                Add New Security Question
              </h2>
              <p className="text-regular text-lighterGrey">
                Select a new question below to answer
              </p>
            </div>
            <CloseOutlined
              onClick={onClose}
              className="cursor-pointer"
              style={{
                color: "var(--veegil-color-primary)",
                backgroundColor: "var(--veegil-color-lighter-blue)",
                borderRadius: "50%",
                padding: "4px",
              }}
            />
          </div>

          {/* Content */}
          <div className="p-5 bg-lighterBlue">
            {isLoading ? (
              <div className="flex justify-center items-center h-40">
                <p className="text-regular text-primary">
                  Loading security questions...
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-5">
                {securityQuestions.map((question, index) => {
                  const isAlreadyAdded = userAddedQuestionIds.includes(
                    question._id
                  );

                  return (
                    <div
                      key={question._id}
                      className={`bg-white rounded p-2.5 h-[45px] flex items-center justify-between
                        ${
                          isAlreadyAdded
                            ? "opacity-70 cursor-not-allowed"
                            : "cursor-pointer hover:text-primary group"
                        }`}
                      onClick={() => handleQuestionSelect(question)}>
                      <span
                        className={`text-regular transition-colors truncate mr-2 ${
                          isAlreadyAdded
                            ? "text-black font-bold"
                            : "text-lighterGrey group-hover:text-black"
                        }`}>
                        {index + 1}. {question.question}
                      </span>
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors shrink-0
                          ${
                            isAlreadyAdded
                              ? "border-primary bg-primary text-white"
                              : "border-grey-200 bg-white text-lighterGrey group-hover:border-primary group-hover:bg-primary group-hover:text-white"
                          }`}>
                        <CheckIcon />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="w-full bg-lighterBlue p-4">
            <div className="text-center">
              <span className="text-regular text-lighterGrey">
                Having trouble with your Verification?{" "}
              </span>
              <a
                href="/help-centre"
                className="text-primary font-medium hover:underline">
                Visit Help Centre
              </a>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

AddSecurityQuestion.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  onSelect: PropTypes.func,
};

export default AddSecurityQuestion;
